/**
 * Kubota mobile drawer state controller.
 *
 * Horizon's stock HeaderDrawer custom element handles the outer details/summary
 * open/close. This controller layers on top of it: it owns the drill state
 * inside the drawer, switching between the top-level list and one of N
 * subpages by setting [data-state] on `.kubota-drawer`.
 *
 * Single source of truth: `data-state` on the drawer root.
 *   - "top"          → top-level list visible; subpages hidden.
 *   - "subpage:X"    → subpage with [data-handle="X"] visible; top-level hidden.
 *
 * On drawer close (Horizon's HeaderDrawer.close()), we reset to "top" so the
 * next open starts fresh.
 */

(() => {
  function init(drawer) {
    if (drawer.dataset.kubotaDrawerWired === 'true') return;
    drawer.dataset.kubotaDrawerWired = 'true';

    // Drill in.
    drawer.addEventListener('click', (event) => {
      const openTrigger = event.target.closest('[data-kubota-drawer-open]');
      if (openTrigger && drawer.contains(openTrigger)) {
        event.preventDefault();
        const handle = openTrigger.dataset.kubotaDrawerOpen;
        setState(drawer, `subpage:${handle}`);
        // ARIA: mark this trigger expanded; clear others.
        drawer.querySelectorAll('[data-kubota-drawer-open]').forEach((btn) => {
          btn.setAttribute('aria-expanded', btn === openTrigger ? 'true' : 'false');
        });
        // Move focus to the matching subpage's back button for keyboard users.
        const subpage = drawer.querySelector(`.kubota-drawer__subpage[data-handle="${CSS.escape(handle)}"]`);
        const back = subpage?.querySelector('[data-kubota-drawer-back]');
        // preventScroll: focusing an off-screen element (the back button on
        // a subpage parked at translateX(100%) at the moment of drill-in)
        // would otherwise trigger the browser to auto-scroll the drawer
        // (which has overflow: hidden but is still a scroll container) to
        // bring the focused element into view — visually exposing the
        // wrong subpage's content. Always-mounted layers depend on the
        // drawer staying at scrollLeft=0; preventScroll keeps it there.
        back?.focus({ preventScroll: true });
        return;
      }

      // Drill back.
      const backTrigger = event.target.closest('[data-kubota-drawer-back]');
      if (backTrigger && drawer.contains(backTrigger)) {
        event.preventDefault();
        backToTop(drawer);
        return;
      }
    });

    // ESC handling. We listen on the drawer (not document) so we don't fight
    // Horizon's HeaderDrawer ESC handling for closing the whole drawer.
    drawer.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape') return;
      const state = drawer.dataset.state || 'top';
      if (state.startsWith('subpage:')) {
        // Eat the event so Horizon's outer ESC handler doesn't also close the
        // whole drawer in the same keystroke.
        event.stopPropagation();
        backToTop(drawer);
      }
      // else: let Horizon's HeaderDrawer handle the close.
    });

    // Reset drill state on outer close + sync [open] attribute to the
    // fade-out's actual completion.
    //
    // Why this is needed: `<details>` natively toggles `[open]` on summary
    // click. Horizon's HeaderDrawer.toggle() reads isOpen from `[open]` to
    // decide which path to take. If `[open]` is still true after a close
    // fade-out completes, the next summary click is interpreted as "close
    // again" instead of "open" and the drawer never re-opens.
    //
    // Horizon's own onAnimationEnd would handle this for stock drawers
    // (selector `.menu-drawer, .menu-drawer__submenu`), but our drawer is
    // `.kubota-drawer` and doesn't match — Horizon falls back to watching
    // `<details>` itself, which has no animations, so its callback resolves
    // before our 250ms fade can play and removes [open] prematurely.
    //
    // The drawer is kept rendered regardless of [open] via CSS
    // (`details > .kubota-drawer { display: block }`), so we can let Horizon
    // remove [open] whenever it wants — but we ALSO ensure [open] is removed
    // exactly once per close cycle, after the fade actually completes.
    const details = drawer.closest('details');
    if (details) {
      let pendingCloseTimer = null;

      const cancelPendingClose = () => {
        if (pendingCloseTimer) {
          clearTimeout(pendingCloseTimer);
          pendingCloseTimer = null;
        }
      };

      const classObserver = new MutationObserver(() => {
        const isOpen = details.classList.contains('menu-open');

        if (!isOpen) {
          // Close started (or class shuffle while already closed). Reset
          // drill state and aria so the next open starts clean. Use
          // snapState so the slide-back is invisible behind the fade-out.
          snapState(drawer, 'top');
          drawer.querySelectorAll('[data-kubota-drawer-open]').forEach((btn) => {
            btn.setAttribute('aria-expanded', 'false');
          });

          // Schedule [open] cleanup after the fade. setTimeout (not
          // transitionend) because opacity transitionend can be skipped
          // by the browser when the element transitions to visibility:hidden
          // mid-fade — leaving us no signal. 300ms covers our 250ms close
          // transition with a small buffer.
          cancelPendingClose();
          pendingCloseTimer = setTimeout(() => {
            pendingCloseTimer = null;
            // Only remove [open] if .menu-open is STILL absent — guards
            // against the user re-opening while the timer was pending and
            // the cancel didn't fire (defensive; cancel should catch it).
            if (!details.classList.contains('menu-open') && details.hasAttribute('open')) {
              details.removeAttribute('open');
            }
          }, 300);
        } else {
          // .menu-open re-added (open or reopen mid-close). Cancel any
          // pending [open] removal so it doesn't fire after the reopen.
          cancelPendingClose();
        }
      });
      classObserver.observe(details, { attributes: true, attributeFilter: ['class'] });
    }

    // Initial state.
    setState(drawer, 'top');
  }

  // State setter — flips attributes only. CSS handles the slide via
  // `transition: transform` between two stable values (translateX(±100%)
  // ↔ translateX(0)). No display toggling, no keyframes, no animationend
  // dance. Mid-flight state changes are absorbed naturally by the CSS
  // transition engine, which interrupts cleanly from the current
  // interpolated transform to the new target.
  function setState(drawer, state) {
    const prevState = drawer.dataset.state || 'top';
    if (prevState === state) return;

    drawer.dataset.state = state;
    const handle = state.startsWith('subpage:') ? state.slice('subpage:'.length) : null;

    // Active subpage gets data-active (slides in to translateX(0)).
    // Every other subpage stays parked at translateX(100%) by base CSS.
    // Inactive layers are made inert + aria-hidden so SR/keyboard skip
    // them while they're parked off-screen.
    drawer.querySelectorAll('.kubota-drawer__subpage').forEach((sub) => {
      const isActive = sub.dataset.handle === handle;
      if (isActive) {
        sub.setAttribute('data-active', '');
        sub.removeAttribute('inert');
        sub.removeAttribute('aria-hidden');
      } else {
        sub.removeAttribute('data-active');
        sub.setAttribute('inert', '');
        sub.setAttribute('aria-hidden', 'true');
      }
    });

    const top = drawer.querySelector('.kubota-drawer__top');
    if (top) {
      if (state === 'top') {
        top.removeAttribute('inert');
        top.removeAttribute('aria-hidden');
      } else {
        top.setAttribute('inert', '');
        top.setAttribute('aria-hidden', 'true');
      }
    }
  }

  // Snap state without transition — used when the drawer is closing,
  // so the user doesn't see a slide-back behind the fade-out.
  function snapState(drawer, state) {
    drawer.setAttribute('data-suppress-transition', '');
    setState(drawer, state);
    // Force a synchronous style/layout commit so the snap is in effect
    // before transitions get re-enabled.
    void drawer.offsetHeight;
    drawer.removeAttribute('data-suppress-transition');
  }

  function backToTop(drawer) {
    setState(drawer, 'top');
    drawer.querySelectorAll('[data-kubota-drawer-open]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
    // Return focus to the originating drill button — find the most recently-
    // active one. Easiest: focus the first drill button if none was active.
    const firstDrill = drawer.querySelector('[data-kubota-drawer-open]');
    firstDrill?.focus({ preventScroll: true });
  }

  function start() {
    document.querySelectorAll('.kubota-drawer').forEach(init);

    // If the drawer DOM is replaced by section hydration, re-init.
    const observer = new MutationObserver(() => {
      document.querySelectorAll('.kubota-drawer:not([data-kubota-drawer-wired])').forEach(init);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
